/**
 * SipTrunkDrawer — create/edit a carrier SIP trunk.
 *
 * Layout
 * ------
 * Hosted in `ResourceDrawer` at `width="xl"` (max-w-4xl) because the form is
 * two-column and tabbed. Fields are split across two tabs:
 *
 *   1. Basic Information  — status, trunk + service-provider identity, and the
 *                           SIP connection (host, credentials, From overrides,
 *                           outbound proxy).
 *   2. Additional Settings— routing flags (default trunk for external
 *                           transfers, SIP registration), transport/port, and
 *                           caller ID.
 *
 * Both panels stay MOUNTED (the inactive one is `hidden`) so react-hook-form
 * keeps every field registered and a validation error on the background tab
 * still exists in the DOM — the tab strip surfaces it with a dot so the admin
 * knows where to look.
 *
 * Backend contract (apps/api/src/routes/admin/sip-trunks/{create,update}.ts)
 * --------------------------------------------------------------------------
 *   - POST  /admin/sip-trunks        body: { name, host, port, transport,
 *                                            username?, secret?, enabled,
 *                                            serviceProvider*, maxCalls,
 *                                            fromUser?, fromDomain?,
 *                                            proxyServer?,
 *                                            defaultForExternalTransfers,
 *                                            sipRegistration } → 201
 *   - PATCH /admin/sip-trunks/:id    body: partial of the same fields. Secret
 *                                    semantics:
 *                                      - field omitted      → keep existing
 *                                      - `secret: null`     → clear
 *                                      - `secret: '<str>'`  → rotate
 *
 * The API NEVER returns the encrypted secret — only `hasSecret: boolean` on
 * read. The plaintext is sent only on save and is never echoed back.
 *
 * Save ownership
 * --------------
 * Props are `{ open, trunk, onClose, onSaved }` (no `onSave`). The drawer
 * performs the POST/PATCH itself, then calls `onSaved()` + `onClose()`.
 * Errors surface via toast and the drawer stays open.
 *
 * Secret field semantics
 * ----------------------
 *   - Create mode: required, 8-256 chars, type=password.
 *   - Edit mode: optional. Placeholder reads "Leave blank to keep current".
 *     Leaving it blank submits WITHOUT a `secret` key, so the backend keeps
 *     the existing envelope. Entering a new value (≥8 chars) rotates the
 *     credential. A "Clear secret" checkbox below it explicitly sends
 *     `secret: null` to clear the credential — when checked, the typed
 *     secret (if any) is ignored.
 *
 * Remount per trunk
 * -----------------
 * `ZodForm` mount-initializes from `defaultValues` and does not re-sync. The
 * inner form is given a `key` of `trunk?.id ?? 'new'` so opening a different
 * trunk — or switching between create and edit — fully remounts it.
 */
import { useRef, useState, type JSX, type ReactNode } from 'react'
import { z } from 'zod'
import ResourceDrawer from '../ResourceDrawer.js'
import {
  SectionCard,
  TabStrip,
  SwitchRow,
  CompactSwitch,
  useDrawerTabs,
} from '../DrawerFormKit.js'
import { SipTrunkAclTab } from './SipTrunkAclTab.js'
import {
  ZodForm,
  FormText,
  FormNumber,
  FormTextarea,
  FormDropdown,
  FormCheckbox,
} from '../../forms/index.js'
import { FormGrid } from '../../forms/layout/index.js'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

/**
 * A SIP trunk as returned by `GET /admin/sip-trunks`. Note that the
 * encrypted credential envelope is NEVER present — the API replaces it with
 * the `hasSecret` boolean.
 */
export interface SipTrunk {
  id: string
  name: string
  host: string
  port: number
  transport: 'udp' | 'tcp' | 'tls'
  username: string | null
  hasSecret: boolean
  callerIdNumber: string | null
  callerIdName: string | null
  enabled: boolean
  serviceProviderName: string | null
  serviceProviderContact: string | null
  serviceProviderSupportDetails: string | null
  maxCalls: number
  fromUser: string | null
  fromDomain: string | null
  proxyServer: string | null
  defaultForExternalTransfers: boolean
  sipRegistration: boolean
  createdAt: string
  updatedAt: string
}

export interface SipTrunkDrawerProps {
  open: boolean
  /** The trunk being edited; `null` = create mode. */
  trunk: SipTrunk | null
  onClose: () => void
  /** Called after a successful save so the parent can refetch + close. */
  onSaved: () => void
}

const TRANSPORT_OPTIONS = [
  { label: 'UDP — plain, most common', value: 'udp' },
  { label: 'TCP — plain, connection-oriented', value: 'tcp' },
  { label: 'TLS — encrypted signalling', value: 'tls' },
] as const

// ---------------------------------------------------------------------------
// Zod schemas
// ---------------------------------------------------------------------------

// Shared field validators — mirror the backend CreateSchema constraints.
const nameField = z.string().trim().min(1, 'Trunk name is required').max(64, 'Max 64 characters')
const hostField = z.string().trim().min(1, 'SIP server host is required').max(255, 'Max 255 characters')
const portField = z
  .number({ message: 'Port is required' })
  .int('Port must be an integer')
  .min(1, 'Port must be ≥ 1')
  .max(65535, 'Port must be ≤ 65535')
const transportField = z.enum(['udp', 'tcp', 'tls'])
const usernameField = z.string().trim().max(128, 'Max 128 characters')
const callerIdNumberField = z.string().trim().max(32, 'Max 32 characters')
const callerIdNameField = z.string().trim().max(64, 'Max 64 characters')
const enabledField = z.boolean()
const providerNameField = z
  .string()
  .trim()
  .min(1, 'Service provider name is required')
  .max(128, 'Max 128 characters')
const providerContactField = z.string().trim().max(128, 'Max 128 characters')
const providerSupportField = z.string().trim().max(512, 'Max 512 characters')
const maxCallsField = z
  .number({ message: 'Maximum number of calls is required' })
  .int('Must be a whole number')
  .min(0, 'Must be ≥ 0')
  .max(10000, 'Must be ≤ 10000')
const fromUserField = z.string().trim().max(128, 'Max 128 characters')
const fromDomainField = z.string().trim().max(255, 'Max 255 characters')
const proxyServerField = z.string().trim().max(255, 'Max 255 characters')

/** Fields shared by create + edit, before the secret rules diverge. */
const baseShape = {
  name: nameField,
  host: hostField,
  port: portField,
  transport: transportField,
  username: usernameField,
  callerIdNumber: callerIdNumberField,
  callerIdName: callerIdNameField,
  enabled: enabledField,
  serviceProviderName: providerNameField,
  serviceProviderContact: providerContactField,
  serviceProviderSupportDetails: providerSupportField,
  maxCalls: maxCallsField,
  fromUser: fromUserField,
  fromDomain: fromDomainField,
  proxyServer: proxyServerField,
  defaultForExternalTransfers: z.boolean(),
  sipRegistration: z.boolean(),
}

/**
 * CREATE schema — secret is required (8-256 chars) per backend
 * CreateSchema's `secret: z.string().min(8).max(256).nullable().optional()`;
 * the spec calls it "required" for create flows so we enforce it client-side.
 */
const CreateSipTrunkSchema = z.object({
  ...baseShape,
  secret: z
    .string()
    .min(8, 'Password must be 8-256 characters')
    .max(256, 'Password must be 8-256 characters'),
})

/**
 * EDIT schema — secret is OPTIONAL. A blank value means "keep existing"
 * (won't be sent to the backend). A non-blank value must be 8-256 chars,
 * UNLESS `clearSecret` is checked — in which case the typed value is
 * ignored by the submit handler, so its length is irrelevant. We enforce
 * that interaction at the top level via `superRefine` so an admin can
 * safely tick "Clear password" even when leftover characters remain in the
 * secret field.
 */
const EditSipTrunkSchema = z
  .object({
    ...baseShape,
    secret: z.string(),
    clearSecret: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (
      !data.clearSecret &&
      data.secret !== '' &&
      (data.secret.length < 8 || data.secret.length > 256)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['secret'],
        message: 'Password must be 8-256 characters',
      })
    }
  })

interface CreateFormValues {
  name: string
  host: string
  port: number
  transport: 'udp' | 'tcp' | 'tls'
  username: string
  secret: string
  callerIdNumber: string
  callerIdName: string
  enabled: boolean
  serviceProviderName: string
  serviceProviderContact: string
  serviceProviderSupportDetails: string
  maxCalls: number
  fromUser: string
  fromDomain: string
  proxyServer: string
  defaultForExternalTransfers: boolean
  sipRegistration: boolean
}

interface EditFormValues extends CreateFormValues {
  clearSecret: boolean
}

/**
 * Which tab owns which field. Used only to decorate a tab with an error dot
 * when validation fails while that tab is in the background.
 */
const BASIC_FIELDS = [
  'name',
  'serviceProviderName',
  'serviceProviderContact',
  'serviceProviderSupportDetails',
  'maxCalls',
  'host',
  'username',
  'secret',
  'fromUser',
  'fromDomain',
  'proxyServer',
] as const
const ADDITIONAL_FIELDS = [
  'defaultForExternalTransfers',
  'sipRegistration',
  'transport',
  'port',
  'callerIdNumber',
  'callerIdName',
] as const
const ACL_FIELDS: readonly string[] = []

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SipTrunkDrawer({
  open,
  trunk,
  onClose,
  onSaved,
}: SipTrunkDrawerProps): JSX.Element {
  const toast = useToast()
  const formRef = useRef<HTMLFormElement>(null)
  const [saving, setSaving] = useState(false)

  const isEdit = trunk !== null
  /** Remount key — changes when a different trunk (or create mode) opens. */
  const contentKey = trunk?.id ?? 'new'

  // --- Default values ------------------------------------------------------

  const createDefaults: CreateFormValues = {
    name: '',
    host: '',
    port: 5060,
    transport: 'udp',
    username: '',
    secret: '',
    callerIdNumber: '',
    callerIdName: '',
    enabled: true,
    serviceProviderName: '',
    serviceProviderContact: '',
    serviceProviderSupportDetails: '',
    maxCalls: 0,
    fromUser: '',
    fromDomain: '',
    proxyServer: '',
    defaultForExternalTransfers: false,
    sipRegistration: false,
  }

  const editDefaults: EditFormValues | null = trunk
    ? {
        name: trunk.name,
        host: trunk.host,
        port: trunk.port,
        transport: trunk.transport,
        username: trunk.username ?? '',
        secret: '',
        clearSecret: false,
        callerIdNumber: trunk.callerIdNumber ?? '',
        callerIdName: trunk.callerIdName ?? '',
        enabled: trunk.enabled,
        serviceProviderName: trunk.serviceProviderName ?? '',
        serviceProviderContact: trunk.serviceProviderContact ?? '',
        serviceProviderSupportDetails: trunk.serviceProviderSupportDetails ?? '',
        maxCalls: trunk.maxCalls ?? 0,
        fromUser: trunk.fromUser ?? '',
        fromDomain: trunk.fromDomain ?? '',
        proxyServer: trunk.proxyServer ?? '',
        defaultForExternalTransfers: trunk.defaultForExternalTransfers ?? false,
        sipRegistration: trunk.sipRegistration ?? false,
      }
    : null

  // --- Save flow -----------------------------------------------------------

  /** Fields that map 1:1 onto the API body, with '' collapsed to null. */
  function commonBody(values: CreateFormValues): Record<string, unknown> {
    return {
      name: values.name,
      host: values.host,
      port: values.port,
      transport: values.transport,
      username: values.username ? values.username : null,
      callerIdNumber: values.callerIdNumber ? values.callerIdNumber : null,
      callerIdName: values.callerIdName ? values.callerIdName : null,
      enabled: values.enabled,
      serviceProviderName: values.serviceProviderName,
      serviceProviderContact: values.serviceProviderContact
        ? values.serviceProviderContact
        : null,
      serviceProviderSupportDetails: values.serviceProviderSupportDetails
        ? values.serviceProviderSupportDetails
        : null,
      maxCalls: values.maxCalls,
      fromUser: values.fromUser ? values.fromUser : null,
      fromDomain: values.fromDomain ? values.fromDomain : null,
      proxyServer: values.proxyServer ? values.proxyServer : null,
      defaultForExternalTransfers: values.defaultForExternalTransfers,
      sipRegistration: values.sipRegistration,
    }
  }

  async function handleCreate(values: CreateFormValues): Promise<void> {
    setSaving(true)
    try {
      await api.post('/admin/sip-trunks', {
        ...commonBody(values),
        secret: values.secret,
      })
      toast.success('SIP trunk created')
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save SIP trunk.')
    } finally {
      setSaving(false)
    }
  }

  async function handleEdit(values: EditFormValues): Promise<void> {
    if (!trunk) return
    setSaving(true)
    try {
      const body = commonBody(values)
      // Secret semantics: clearSecret wins over a typed value. Otherwise,
      // only include `secret` when the admin actually typed one — blank
      // means "leave the existing credential alone".
      if (values.clearSecret) {
        body['secret'] = null
      } else if (values.secret) {
        body['secret'] = values.secret
      }
      await api.patch(`/admin/sip-trunks/${trunk.id}`, body)
      toast.success('SIP trunk updated')
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save SIP trunk.')
    } finally {
      setSaving(false)
    }
  }

  // --- Render --------------------------------------------------------------

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={async () => {}}
      formRef={formRef}
      saving={saving}
      width="xl"
      title={trunk ? `Edit SIP Trunk: ${trunk.name}` : 'New SIP Trunk'}
    >
      {isEdit && editDefaults ? (
        <ZodForm
          key={contentKey}
          schema={EditSipTrunkSchema}
          defaultValues={editDefaults}
          onSubmit={(data) => handleEdit(data as EditFormValues)}
          formRef={formRef}
        >
          <TrunkTabs
            trunkId={trunk?.id ?? null}
            secretField={
              <FormText
                name="secret"
                label="Password"
                type="password"
                placeholder="Leave blank to keep current"
                helpText="Enter a new 8-256 character password to rotate, or leave blank to keep the existing credential."
              />
            }
            secretExtras={
              <>
                {trunk.hasSecret && (
                  <p
                    className="-mt-2 flex items-center gap-1.5 text-xs text-gray-500"
                    role="note"
                  >
                    <i className="pi pi-lock text-[10px]" aria-hidden="true" />
                    Password stored — the encrypted credential is on file.
                  </p>
                )}
                <FormCheckbox
                  name="clearSecret"
                  label="Clear password"
                  helpText="Remove the stored credential entirely (sends secret: null)."
                />
              </>
            }
          />
        </ZodForm>
      ) : (
        <ZodForm
          key={contentKey}
          schema={CreateSipTrunkSchema}
          defaultValues={createDefaults}
          onSubmit={(data) => handleCreate(data as CreateFormValues)}
          formRef={formRef}
        >
          <TrunkTabs
            trunkId={trunk?.id ?? null}
            secretField={
              <FormText
                name="secret"
                label="Password"
                type="password"
                required
                placeholder="8-256 characters"
                helpText="The SIP registration password. Stored encrypted at rest."
              />
            }
          />
        </ZodForm>
      )}
    </ResourceDrawer>
  )
}

// ---------------------------------------------------------------------------
// Tab shell
// ---------------------------------------------------------------------------

/**
 * The two-tab body. Lives inside `ZodForm` so it can read `formState.errors`
 * via context and flag the tab that holds a failing field.
 *
 * `secretField` (and, in edit mode, `secretExtras`) are injected because the
 * password control differs between create (required) and edit (optional +
 * clear checkbox).
 */
function TrunkTabs({
  secretField,
  secretExtras,
  trunkId,
}: {
  secretField: ReactNode
  secretExtras?: ReactNode
  trunkId: string | null
}): JSX.Element {
  const { active, setActive, errorFor } = useDrawerTabs([BASIC_FIELDS, ADDITIONAL_FIELDS, ACL_FIELDS])

  return (
    <div className="kc3-form">
      <TabStrip
        idPrefix="sip-trunk"
        ariaLabel="SIP trunk settings"
        activeIndex={active}
        onChange={setActive}
        tabs={[
          {
            key: 'basic',
            label: 'Basic Information',
            icon: 'pi pi-info-circle',
            error: errorFor(0),
          },
          {
            key: 'additional',
            label: 'Additional Settings',
            icon: 'pi pi-sliders-h',
            error: errorFor(1),
          },
          {
            key: 'acl',
            label: 'ACL',
            icon: 'pi pi-shield',
            error: errorFor(2),
          },
        ]}
      />

      {/* Both panels stay mounted so RHF keeps every field registered. */}
      <div
        id="sip-trunk-panel-basic"
        role="tabpanel"
        aria-labelledby="sip-trunk-tab-basic"
        hidden={active !== 0}
        className="space-y-5"
      >
        <SectionCard
          title="Trunk & Service Provider"
          description="How this trunk is identified and who to contact when it breaks."
          icon="pi pi-id-card"
          // Status rides in the section header rather than a card of its own —
          // a lone full-width toggle strip leaves a wide empty gutter between
          // its label and its switch at this drawer width.
          action={
            <CompactSwitch
              name="enabled"
              label="Status"
              onLabel="Active"
              offLabel="Inactive"
              title="Inactive trunks are excluded from outbound routing."
            />
          }
        >
          <FormGrid columns={2}>
            <FormText
              name="name"
              label="Trunk Name"
              required
              autoFocus
              placeholder="Carrier US-East"
              helpText="A human-readable name for this trunk (1-64 characters)."
            />
            <FormText
              name="serviceProviderName"
              label="Service Provider Name"
              required
              placeholder="Acme Telecom"
              helpText="The carrier or ITSP supplying this trunk."
            />
            <FormText
              name="serviceProviderContact"
              label="Service Provider Contact"
              placeholder="+1 858 312 7722 / noc@acme.example"
              helpText="Account manager or NOC contact."
            />
            <FormNumber
              name="maxCalls"
              label="Maximum Number Of Calls"
              min={0}
              max={10000}
              useGrouping={false}
              helpText="Concurrent-call cap for this trunk. 0 means unlimited."
            />
          </FormGrid>
          <FormTextarea
            name="serviceProviderSupportDetails"
            label="Service Provider Support Details"
            rows={3}
            placeholder="Support hours, escalation path, ticket portal, account number…"
            helpText="Free-form notes shown to admins troubleshooting this trunk (max 512 characters)."
          />
        </SectionCard>

        <SectionCard
          title="SIP Connection"
          description="Where calls are sent and how this trunk authenticates."
          icon="pi pi-server"
        >
          <FormGrid columns={2}>
            <FormText
              name="host"
              label="SIP Server Host"
              required
              placeholder="sip.carrier.example.com"
              helpText="FQDN or IP address of the carrier SIP edge."
            />
            <FormText
              name="username"
              label="Username"
              placeholder="sip_user"
              helpText="Optional SIP auth username (max 128 characters)."
            />
            <div>
              {secretField}
              {secretExtras}
            </div>
            <FormText
              name="proxyServer"
              label="Proxy Server"
              placeholder="proxy.carrier.example.com"
              helpText="Outbound proxy, if the carrier routes signalling through one."
            />
            <FormText
              name="fromUser"
              label="From User"
              placeholder="18583127722"
              helpText="Overrides the user part of the outbound From header. Blank = use the username."
            />
            <FormText
              name="fromDomain"
              label="From Domain"
              placeholder="carrier.example.com"
              helpText="Overrides the domain part of the outbound From header. Blank = use the host."
            />
          </FormGrid>
        </SectionCard>
      </div>

      <div
        id="sip-trunk-panel-additional"
        role="tabpanel"
        aria-labelledby="sip-trunk-tab-additional"
        hidden={active !== 1}
        className="space-y-5"
      >
        <SectionCard
          title="Routing"
          description="How this trunk participates in transfers and registration."
          icon="pi pi-directions"
          // Toggles are a divided list, not a field grid — they supply their
          // own row padding, so the card body adds none.
          bodyClassName="divide-y divide-gray-100 px-5"
        >
          <SwitchRow
            name="defaultForExternalTransfers"
            label="Default Trunk For External Transfers"
            description="Agent-initiated transfers to an outside number use this trunk when no more specific route matches. Only one trunk can hold this — enabling it here clears it elsewhere."
          />
          <SwitchRow
            name="sipRegistration"
            label="SIP Registration"
            description="Register with the carrier using the username and password above. Leave off for IP-authenticated trunks."
          />
        </SectionCard>

        <SectionCard
          title="Transport"
          description="Signalling transport and port for this trunk."
          icon="pi pi-share-alt"
        >
          <FormGrid columns={2}>
            <FormDropdown
              name="transport"
              label="Transport"
              required
              options={[...TRANSPORT_OPTIONS]}
              placeholder="Select a transport"
              helpText="Must match what the carrier expects. TLS also usually needs port 5061."
            />
            <FormNumber
              name="port"
              label="Port"
              min={1}
              max={65535}
              required
              useGrouping={false}
              helpText="Carrier signalling port — 5060 for UDP/TCP, 5061 for TLS."
            />
          </FormGrid>
        </SectionCard>

        <SectionCard
          title="Caller ID"
          description="What the called party sees on outbound calls over this trunk."
          icon="pi pi-user"
        >
          <FormGrid columns={2}>
            <FormText
              name="callerIdNumber"
              label="Caller ID Number"
              placeholder="18583127722"
              helpText="E.164 number to present on outbound calls (max 32 characters)."
            />
            <FormText
              name="callerIdName"
              label="Caller ID Name"
              placeholder="Telerain"
              helpText="Display name to present on outbound calls (max 64 characters)."
            />
          </FormGrid>
        </SectionCard>
      </div>

      <div
        id="sip-trunk-panel-acl"
        role="tabpanel"
        aria-labelledby="sip-trunk-tab-acl"
        hidden={active !== 2}
        className="space-y-5"
      >
        <SectionCard
          title="IP Access Control"
          description="ACLs that allow inbound traffic for this trunk."
          icon="pi pi-shield"
        >
          <SipTrunkAclTab trunkId={trunkId} />
        </SectionCard>
      </div>
    </div>
  )
}
